import { Button } from "@studyplus/boron-ui";
import queryString from "query-string";
import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Link, useLocation } from "react-router-dom";
import Loader from "../../components/atoms/Loader";
import Table, { TableColumn } from "../../components/atoms/Table";
import { CopyToClipboard } from "../../components/general/CopyToClipboard";
import { SearchInput } from "../../components/general/SearchInput";
import TimeHelper from "../../helpers/TimeHelper";
import { useQueryError } from "../../hooks/http/useQueryError";
import { usePushHistory } from "../../hooks/usePushHistory";
import {
  type DrillLearningMaterial,
  useDrillLearningMaterials,
} from "./useDrillLearningMaterials";

const useSearchText = (name: string | null) => {
  const [searchText, setSearchText] = useState<string>("");

  const onChangeSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const onResetSearchText = () => {
    setSearchText("");
  };

  useEffect(() => {
    setSearchText(name || "");
  }, [name]);

  const { pushHistory } = usePushHistory();

  const onSearch = () => {
    const nameParam = searchText ? { name: searchText } : { name: undefined };
    pushHistory(nameParam);
  };

  return {
    searchText,
    onChangeSearchText,
    onResetSearchText,
    onSearch,
  };
};

const CopyIcon = ({ text }: { text: string }) => {
  const [active, setActive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleCopy = useCallback(() => {
    clearTimeout(timerRef.current);
    setActive(true);
    timerRef.current = setTimeout(() => setActive(false), 2000);
  }, []);

  return (
    <CopyToClipboard text={text} onCopy={handleCopy}>
      <button
        type="button"
        className={`${active ? "text-blue-400" : "text-black"} hover:text-blue-400`}
        aria-label="コードをコピー"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.667 0.667H2.667C1.934 0.667 1.334 1.267 1.334 2V11.333H2.667V2H10.667V0.667ZM12.667 3.333H5.334C4.6 3.333 4 3.933 4 4.667V14C4 14.733 4.6 15.333 5.334 15.333H12.667C13.4 15.333 14 14.733 14 14V4.667C14 3.933 13.4 3.333 12.667 3.333ZM12.667 14H5.334V4.667H12.667V14Z"
            fill="currentColor"
          />
        </svg>
      </button>
    </CopyToClipboard>
  );
};

const MaterialRow = ({ material }: { material: DrillLearningMaterial }) => {
  return (
    <tr key={material.steakMaterialCode}>
      <TableColumn>
        <div className="flex items-start gap-6">
          <img
            src={material.imageUrl}
            alt={material.name}
            className="h-12 w-9 shrink-0 rounded-sm object-contain"
          />
          <div className="flex flex-col gap-2.5">
            <span className="text-[1.3rem] font-bold">{material.name}</span>
            <span className="text-sm">セクション：{material.sectionCount}</span>
          </div>
        </div>
      </TableColumn>
      <TableColumn centering>
        <div className="flex items-center justify-center gap-2.5">
          <span className="text-[13px]">{material.drillRegistrationCode}</span>
          <CopyIcon text={material.drillRegistrationCode} />
        </div>
      </TableColumn>
      <TableColumn centering>
        {TimeHelper.fullFormat(material.updatedAt)}
      </TableColumn>
      <TableColumn centering>
        <Link
          to={`/multi_section_batches/update_drill/${material.steakMaterialCode}`}
          className="buiButtonBase buiButtonVariantOutline buiButtonSizeSm buiButtonRound"
        >
          更新
        </Link>
      </TableColumn>
    </tr>
  );
};

export const ListTab = () => {
  const { search } = useLocation();
  const name = queryString.parse(search, { arrayFormat: "bracket" }).name as
    | string
    | null;

  const { searchText, onChangeSearchText, onResetSearchText, onSearch } =
    useSearchText(name);

  const {
    materials,
    isLoading,
    hasNextPage,
    fetchBatch,
    isFetchingNextPage,
    error,
  } = useDrillLearningMaterials({
    name: name ?? undefined,
  });
  useQueryError(error);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  const { ref: sentinelRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchBatch();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchBatch]);

  return (
    <div className="mt-7 flex flex-col gap-4">
      <div className="flex w-[59.5rem] max-w-full gap-4">
        <SearchInput
          isBlock
          placeholder="教材名を入力"
          value={searchText}
          onChange={onChangeSearchText}
          onReset={onResetSearchText}
          onKeyDown={handleKeyDown}
        />
        <Button
          onClick={onSearch}
          isLoading={isLoading}
          className="w-40 shrink-0"
        >
          検索
        </Button>
      </div>
      {isLoading ? (
        <Loader loading={isLoading} />
      ) : (
        <>
          <Table
            headers={[
              { text: "教材名", centering: true },
              { text: "デジタル教材登録コード", centering: true },
              { text: "更新日", centering: true },
              { text: "", centering: true },
            ]}
            rows={materials.map((material) => (
              <MaterialRow
                key={material.steakMaterialCode}
                material={material}
              />
            ))}
            noRowsText={name ? "検索結果が存在しません。" : "登録がありません"}
          />
          <Loader loading={isFetchingNextPage} />
          <div ref={sentinelRef} />
        </>
      )}
    </div>
  );
};
